import type { CSSProperties, ReactNode } from "react";
import { colors, fontSize, fontWeight, radius, shadow } from "../lib/tokens";
import { SkeletonLine } from "./Skeleton";

export interface Column<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  align?: "left" | "right";
  width?: string | number;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  loading?: boolean;
  loadingRows?: number;
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
}

export function DataTable<T>({ columns, rows, loading, loadingRows = 5, onRowClick, empty }: DataTableProps<T>) {
  const wrap: CSSProperties = {
    background: colors.white,
    border: `1px solid ${colors.fog}`,
    borderRadius: radius.md,
    overflowX: "auto",
    boxShadow: shadow.xs,
    WebkitOverflowScrolling: "touch",
  };
  const table: CSSProperties = {
    width: "100%",
    minWidth: 480,
    borderCollapse: "collapse",
    fontSize: fontSize.md,
  };
  const th: CSSProperties = {
    textAlign: "left",
    fontSize: fontSize.xs,
    fontWeight: fontWeight.semibold,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: colors.stone,
    padding: "12px 16px",
    background: colors.paper,
    borderBottom: `1px solid ${colors.fog}`,
  };
  const td: CSSProperties = {
    padding: "14px 16px",
    borderBottom: `1px solid ${colors.fog}`,
    color: colors.ink,
    verticalAlign: "middle",
  };

  if (loading) {
    return (
      <div style={wrap}>
        <table style={table}>
          <thead><tr>
            {columns.map((c) => (
              <th key={c.key} style={{ ...th, textAlign: c.align ?? "left", width: c.width }}>{c.header}</th>
            ))}
          </tr></thead>
          <tbody>
            {Array.from({ length: loadingRows }).map((_, i) => (
              <tr key={i}>
                {columns.map((c) => (
                  <td key={c.key} style={{ ...td, textAlign: c.align ?? "left" }}>
                    <SkeletonLine width={c.align === "right" ? 60 : "70%"} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div style={wrap}>
        <div style={{ padding: 48, textAlign: "center", color: colors.stone, fontSize: fontSize.md }}>
          {empty ?? "Sem registros."}
        </div>
      </div>
    );
  }

  return (
    <div style={wrap}>
      <table style={table}>
        <thead><tr>
          {columns.map((c) => (
            <th key={c.key} style={{ ...th, textAlign: c.align ?? "left", width: c.width }}>{c.header}</th>
          ))}
        </tr></thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              style={{ cursor: onRowClick ? "pointer" : "default", transition: "background 160ms cubic-bezier(0.32, 0.72, 0, 1)" }}
              onMouseEnter={onRowClick ? (e) => (e.currentTarget.style.background = colors.paper) : undefined}
              onMouseLeave={onRowClick ? (e) => (e.currentTarget.style.background = "transparent") : undefined}
            >
              {columns.map((c) => (
                <td key={c.key} style={{ ...td, textAlign: c.align ?? "left", fontFeatureSettings: c.align === "right" ? '"tnum" on' : undefined }}>
                  {c.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
